// CatalogTab.js
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Empty, message, notification } from 'antd';
import { useMutation, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import {
  getFilteredCatalogs,
  handleModalTreeSearchWrapper,
  addNewChildFromSearch,
} from 'helpers/contentlibrary';
import { GET_ALL_CATALOGS_LIBRARIES } from 'graphql/queries/contentLibraryQueries';
import {
  UPDATE_CONTENT_LIBRARY,
  CONTENT_LIBRARY_CATALOG_DELETE,
  CONTENT_LIBRARY_ADD_CHILD,
} from 'graphql/mutations/contentLibraryMutations';
import { CATALOGS_LIMIT } from 'constants/index';
import { ContentLoader } from '../../ContentLoader/ContentLoader';
import { useContentLibrary } from 'context/ContentLibraryContext';

import ConfirmModal from 'components/ConfirmModal';
import ShareWithModal from 'components/ShareWithModal';
import CatalogList from '../CatalogList/CatalogList';
import ContentLibraryTree from '../../ContentLibraryTree/ContentLibraryTree';
import ContentLibraryModals from '../../../ContentLibraryModals';
import CatalogAddProduct from '../CatalogAddProduct/CatalogAddProduct';
import CatalogSyncModal from '../../../ContentLibraryModals/CatalogSyncModal/CatalogSyncModal';

import './CatalogTab.scss';

/**
 * Catalog tab
 *
 * @param {Object} props
 * @param {boolean} props.disabledFilter
 * @param {string} props.searchValue
 * @param {Object} props.user
 * @param {Object} props.config
 * @param {Function} props.loadConfig
 * @param {Function} props.saveUser
 * @param {Array} props.teamMembers
 * @param {string} props.sortValue
 * @param {boolean} props.isLocked
 * @param {boolean} props.showAddNewProductPage
 * @param {Function} props.setShowAddNewProductPage
 * @param {Object} props.addProductForm
 * @param {Function} props.onUploadClick
 * @param {Array} props.catalogThumbnails
 * @param {Function} props.setCatalogThumbnails
 * @param {boolean} props.showImportCatalogModal
 * @param {Function} props.setShowImportCatalogModal
 */
const CatalogTab = forwardRef(
  (
    {
      type,
      disabledFilter,
      searchValue,
      user,
      config,
      loadConfig,
      teamMembers,
      sortValue,
      isLocked,
      showAddNewProductPage,
      setShowAddNewProductPage,
      addProductForm,
      onUploadClick,
      catalogThumbnails,
      setCatalogThumbnails,
      showImportCatalogModal,
      setShowImportCatalogModal,
      showCatalogSyncModal,
      setShowCatalogSyncModal,
      selectedCatalog,
      setSelectedCatalog,
    },
    ref
  ) => {
    // Get treeData and related functions from context
    const {
      treeData,
      handleTreeData,
      setIndexes,
      initialTreeData,
      handleRefetch,
      handleTreeSearch,
      filterTreeData,
      onContentLibraryKeySelect,
      selectedFolderKey,
      addKey,
    } = useContentLibrary();

    /**
     * Filtered tree data
     */
    const filteredTreeData = filterTreeData(treeData, 'catalogs');

    const [catalogs, setCatalogs] = useState([]);
    const [selectedCatalogId, setSelectedCatalogId] = useState('');
    const [cursor, setCursor] = useState(null);
    const [openFromSyncModal, setOpenFromSyncModal] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [confirmModal, showConfirmModal] = useState(false);
    const [shareModal, showShareModal] = useState(false);
    const [moveModal, showMoveModal] = useState(false);
    const [showDuplicateCatalog, showDuplicateCatalogModal] = useState(false);
    const [selectedCatalogEditValue, setSelectedCatalogEditValue] = useState('');
    const [selectedCatalogs, setSelectedCatalogs] = useState([]);
    const [checkedCatalogs, setCheckedCatalogs] = useState(false);
    const [treeSearch, setTreeSearch] = useState('');
    const [modalTreeData, setModalTreeData] = useState({ children: [] });
    const [buttonLoading, setButtonLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);

    const contentLibraryTreeRef = useRef();

    const [fetchAllCatalogs, { refetch, fetchMore, loading }] = useLazyQuery(
      GET_ALL_CATALOGS_LIBRARIES,
      {
        variables: {
          limit: CATALOGS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
          cursor: null,
          search: searchValue,
          key: selectedFolderKey,
          filterItem: 'all',
        },
        fetchPolicy: 'no-cache',
        skip: !!catalogs.length,
        onCompleted: ({ fetchContentLibraryCatalogsItems }) => {
          if (fetchContentLibraryCatalogsItems?.catalogs) {
            setCatalogs(fetchContentLibraryCatalogsItems?.catalogs?.slice());
            setCursor(fetchContentLibraryCatalogsItems.pagingMetadata?.cursor || null);
            setHasNextPage(fetchContentLibraryCatalogsItems.pagingMetadata?.hasNext || false);
          }
        },
      }
    );

    // Update modal tree data when context tree data changes
    useEffect(() => {
      setModalTreeData(filteredTreeData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData]);

    /**
     * Use effect
     */
    useEffect(() => {
      fetchAllCatalogs();
    }, [fetchAllCatalogs]);

    /**
     * Use imperative handle
     */
    useImperativeHandle(ref, () => ({
      reloadItems: () => {
        refetchCatalogs({
          key: selectedFolderKey,
          search: searchValue,
          filterItems: 'all',
          sort: sortValue,
        });
      },
    }));

    /**
     * Refetch catalogs
     *
     * @param {Object} params
     * @param {string} params.key
     * @param {string} params.search
     * @param {Array} params.filter
     * @param {string} params.sort
     */
    const refetchCatalogs = useCallback(
      async ({ key, search }) => {
        if (user?.paymentStatus?.canLock) {
          return;
        }
        const refetchCatalogData = await refetch?.({
          limit: CATALOGS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
          cursor: null,
          search,
          filterItem: 'all',
          key,
        });
        if (refetchCatalogData?.data?.fetchContentLibraryCatalogsItems) {
          setCatalogs(refetchCatalogData?.data?.fetchContentLibraryCatalogsItems.catalogs?.slice());
          setCursor(
            refetchCatalogData.data.fetchContentLibraryCatalogsItems.pagingMetadata?.cursor || null
          );
          setHasNextPage(
            refetchCatalogData.data.fetchContentLibraryCatalogsItems.pagingMetadata?.hasNext ||
              false
          );

          if (!showDuplicateCatalog && !moveModal) {
            setSelectedCatalogs([]);
          }
        }
      },
      [refetch, showDuplicateCatalog, moveModal, user?.paymentStatus?.canLock]
    );

    /**
     * Fetch more catalogs
     *
     */
    const fetchMoreCatalogs = useCallback(async () => {
      if (hasNextPage && cursor && fetchMore && !isLoadingMore) {
        setIsLoadingMore(true);

        const fetchMoreCatalogData = await fetchMore({
          variables: {
            limit: CATALOGS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
            cursor: cursor,
            search: searchValue,
            filterItem: 'all',
            key: selectedFolderKey,
          },
        });

        if (fetchMoreCatalogData?.data?.fetchContentLibraryCatalogsItems) {
          setCatalogs((prevCatalogs) => [
            ...prevCatalogs,
            ...fetchMoreCatalogData.data.fetchContentLibraryCatalogsItems.catalogs,
          ]);
          setCursor(
            fetchMoreCatalogData.data.fetchContentLibraryCatalogsItems.pagingMetadata?.cursor ||
              null
          );
          setHasNextPage(
            fetchMoreCatalogData.data.fetchContentLibraryCatalogsItems.pagingMetadata?.hasNext ||
              false
          );
        }
        setIsLoadingMore(false);
      }
    }, [hasNextPage, cursor, fetchMore, isLoadingMore, searchValue, selectedFolderKey]);
    /**
     * Use effect
     */
    useEffect(() => {
      const mainContent = document.querySelector('.catalog-list-container');
      if (!mainContent) {
        return;
      }

      async function onScroll() {
        if (mainContent?.scrollTop + mainContent?.offsetHeight + 1 > mainContent?.scrollHeight) {
          fetchMoreCatalogs();
        }
      }

      mainContent?.addEventListener('scroll', onScroll);

      return () => {
        mainContent?.removeEventListener('scroll', onScroll);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, catalogs, isLoadingMore]);

    /**
     * Update content item
     */
    const [updateContentItem] = useMutation(UPDATE_CONTENT_LIBRARY, {
      onCompleted() {
        refetchCatalogs({
          key: selectedFolderKey,
          search: searchValue,
          filterItem: 'all',
          sort: sortValue,
        });
      },
    });

    /**
     * Delete catalog
     */
    const [deleteCatalog] = useMutation(CONTENT_LIBRARY_CATALOG_DELETE, {
      onCompleted() {
        setButtonLoading(false);
        setCatalogs(catalogs.filter((catalog) => catalog._id !== selectedCatalogId));
        message.success('Product deleted successfully');
        showConfirmModal(false);
        setSelectedCatalogId('');
        setSelectedCatalogs([]);
        refetchCatalogs({
          key: selectedFolderKey,
          search: searchValue,
          filterItem: 'all',
          sort: sortValue,
        });
        setCheckedCatalogs(false);
      },
      onError() {
        setButtonLoading(false);
        message.error('Product deletion failed');
        showConfirmModal(false);
        setSelectedCatalogs([]);
        setCheckedCatalogs(false);
      },
    });

    /**
     * Use effect
     */
    useEffect(() => {
      if (searchValue || user.deletedTemplates) {
        refetchCatalogs({
          key: selectedFolderKey,
          search: searchValue,
          filterItem: 'all',
          sort: sortValue,
        });
      }
    }, [searchValue, sortValue, refetchCatalogs, selectedFolderKey, user.deletedTemplates]);

    /**
     * Filtered catalogs
     */
    const filteredCatalogs = getFilteredCatalogs({ catalogs: catalogs, searchValue });

    const { all: allItems } = filteredCatalogs;

    /**
     * Confirm delete catalog
     */
    const confirmDeleteCatalog = () => {
      if (!buttonLoading) {
        setButtonLoading(true);
      }
      deleteCatalog({
        variables: {
          _id: selectedCatalogId,
          _ids: selectedCatalogs,
          folderKey: selectedFolderKey,
        },
      });
    };

    /**
     * On click menu item
     *
     * @param {Object} params
     * @param {string} params.actionType
     * @param {string} params.catalogId
     * @param {boolean} params.starred
     * @param {Object} params.catalogData
     * @param {string} params.catalogName
     */
    const onClickMenuItem = async ({ actionType, catalogId, catalogData }) => {
      switch (actionType) {
        case 'delete':
          setSelectedCatalogId(catalogId);
          showConfirmModal(true);
          break;
        case 'edit':
          setSelectedCatalog(catalogData);
          setShowAddNewProductPage(true);
          break;
        case 'save':
          await updateContentItem({
            variables: {
              id: catalogId,
              type: 'catalogs',
              item: { catalogName: selectedCatalogEditValue },
            },
          });
          setSelectedCatalogEditValue('');
          break;
        case 'share':
          setSelectedCatalog(catalogData);
          showShareModal(true);
          break;
        case 'duplicate':
          setSelectedCatalog(catalogData);
          showDuplicateCatalogModal(true);
          break;
        case 'select':
          if (selectedCatalogs.includes(catalogId)) {
            setSelectedCatalogs(selectedCatalogs.filter((id) => id !== catalogId));
          } else {
            setSelectedCatalogs([...selectedCatalogs, catalogId]);
          }
          break;
        case 'select-all':
          if (selectedCatalogs.length === catalogs?.length) {
            setSelectedCatalogs([]);
          } else {
            setSelectedCatalogs(catalogs?.map((catalog) => catalog._id));
          }
          break;
        case 'bulk-duplicate':
          showDuplicateCatalogModal(true);
          break;
        case 'bulk-move':
          showMoveModal(true);
          break;
        case 'bulk-delete':
          showConfirmModal(true);
          break;
        default:
          break;
      }
    };

    /**
     * On select tree item
     *
     * @param {Object} selectedTree
     */
    const onSelectTreeItem = (selectedTree) => {
      addProductForm.setFieldsValue({
        folderKey: selectedTree?.[0]?.[0],
      });
    };

    /**
     * Add child mutation
     */
    const [addChild] = useMutation(CONTENT_LIBRARY_ADD_CHILD, {
      onCompleted() {
        notification.success({
          description: 'Created new folder successfully.',
        });
        handleRefetch(treeSearch, onSelectTreeItem);
        setTreeSearch('');
      },
      onError() {
        setTreeSearch('');
      },
    });

    const onCancelShareModal = () => {
      showShareModal(false);
      setSelectedCatalog(null);
    };

    const onCancelDuplicateCatalog = () => {
      showDuplicateCatalogModal(false);
      setSelectedCatalog(null);
    };

    const onCancelConfirmModal = () => {
      showConfirmModal(false);
      setSelectedCatalog(null);
      setCheckedCatalogs(false);
      setSelectedCatalogs([]);
    };

    const onKeySelect = (selectedFolderKey, isMoveModal) => {
      if (!isMoveModal) {
        onContentLibraryKeySelect(selectedFolderKey);
        setCursor(null);
        setHasNextPage(false);
        setCatalogs([]);
      }

      onSelectTreeItem({
        key: selectedFolderKey?.map(([v]) => v)?.[0],
        treeId: treeData._id,
        name: selectedFolderKey?.[0]?.[2] || '',
      });
    };

    /**
     * Handle modal tree search
     *
     * @param {string} tempSearchValue
     */
    const handleModalTreeSearch = (tempSearchValue) =>
      handleModalTreeSearchWrapper({
        tempSearchValue,
        treeData: filteredTreeData,
        setIndexes,
        initialTreeData,
        addKey,
        setModalTreeData,
        filteredTreeData,
      });

    /**
     * Render add new child from search
     *
     * @param {string} treeSearchValue
     * @param {string} localSelectedFolderKey
     */
    const renderAddNewChildFromSearch = (treeSearchValue, localSelectedFolderKey) =>
      addNewChildFromSearch({
        treeSearchValue,
        selectedFolderKey: localSelectedFolderKey || selectedFolderKey,
        user,
        treeData: modalTreeData,
        addChild,
      });

    /**
     * Render
     */
    return (
      <>
        {showAddNewProductPage ? (
          <CatalogAddProduct
            product={selectedCatalog}
            treeData={filteredTreeData}
            addProductForm={addProductForm}
            onUploadClick={onUploadClick}
            catalogThumbnails={catalogThumbnails}
            selectedFolderKey={selectedFolderKey}
            user={user}
            setCatalogThumbnails={setCatalogThumbnails}
            handleTreeSearch={(tempSearchValue) =>
              handleTreeSearch({
                type: 'catalogs',
                treeData: filteredTreeData,
                tempSearchValue,
                setIndexes,
                handleTreeData,
                initialTreeData,
              })
            }
            addNewChildFromSearch={renderAddNewChildFromSearch}
            initialTreeData={initialTreeData}
            onSelectTreeItem={onSelectTreeItem}
            setTreeSearch={setTreeSearch}
          />
        ) : (
          <div className="content-library-item">
            <ContentLibraryTree
              ref={contentLibraryTreeRef}
              type="catalogs"
              onUpdate={() =>
                refetchCatalogs({
                  key: selectedFolderKey,
                  search: searchValue,
                  filterItems: 'all',
                  sort: sortValue,
                })
              }
              userId={user && user._id}
              userRole={user?.roles && user?.roles[0]}
              isLocked={isLocked}
              treeData={treeData}
              onKeySelect={onKeySelect}
            />

            <div className="split-d2 catalog-list-container">
              {loading ? (
                <ContentLoader className="catalog-list-loader" />
              ) : (
                <>
                  <React.Fragment>
                    {allItems.length === 0 ? (
                      <div className="item-row empty-wrapper">
                        <Empty
                          className="empty-view"
                          description="Looks like you have no catalog files yet"
                        />
                      </div>
                    ) : (
                      <>
                        <CatalogList
                          checkedCatalogs={checkedCatalogs}
                          setCheckedCatalogs={setCheckedCatalogs}
                          catalogs={allItems}
                          onClickMenuItem={onClickMenuItem}
                          user={user}
                          isLocked={isLocked}
                          config={config}
                          loadConfig={loadConfig}
                          disabledFilter={disabledFilter}
                          treeData={filteredTreeData}
                          selectedCatalogs={selectedCatalogs}
                          refetchCatalogs={() =>
                            refetchCatalogs({
                              key: selectedFolderKey,
                              search: searchValue,
                              filterItems: 'all',
                              sort: sortValue,
                            })
                          }
                        />
                      </>
                    )}

                    {isLoadingMore && <ContentLoader length={4} className="catalog-list-loader" />}
                  </React.Fragment>
                </>
              )}
            </div>
            {shareModal && (
              <ShareWithModal
                onCancel={onCancelShareModal}
                catalog={selectedCatalog}
                teamMembers={teamMembers}
                visible={shareModal}
                isLocked={isLocked}
                isCatalog
              />
            )}
            {confirmModal && (
              <ConfirmModal
                title="Delete Product?"
                body="This action cannot be undone"
                visible={confirmModal}
                isSaving={buttonLoading}
                cancelText="CANCEL"
                confirmText="YES, DELETE"
                onCancel={onCancelConfirmModal}
                onConfirm={confirmDeleteCatalog}
              />
            )}

            <ContentLibraryModals
              visible={moveModal || showDuplicateCatalog}
              onCancel={
                showDuplicateCatalog ? onCancelDuplicateCatalog : () => showMoveModal(false)
              }
              catalog={selectedCatalog}
              treeData={modalTreeData}
              setTreeSearch={setTreeSearch}
              selectedFolderKey={selectedFolderKey}
              catalogIds={selectedCatalogs}
              isCatalog={`${showDuplicateCatalog ? 'Duplicate' : 'Catalog'}`}
              showImportCatalogModal={showImportCatalogModal}
              setShowImportCatalogModal={(value) => {
                setShowImportCatalogModal(value);
                if (!value) {
                  // Reset flag when modal closes
                  setOpenFromSyncModal(false);
                }
              }}
              setCheckedCatalogs={setCheckedCatalogs}
              onKeySelect={onKeySelect}
              handleTreeSearch={handleModalTreeSearch}
              addNewChildFromSearch={renderAddNewChildFromSearch}
              refetchCatalogs={() =>
                refetchCatalogs({
                  key: selectedFolderKey,
                  search: searchValue,
                  filterItems: 'all',
                  sort: sortValue,
                })
              }
              defaultCatalogSource={openFromSyncModal ? 'xml' : 'csv'}
              defaultSaveXmlLink={openFromSyncModal}
            />

            <CatalogSyncModal
              visible={showCatalogSyncModal}
              onCancel={() => setShowCatalogSyncModal(false)}
              setShowCatalogSyncModal={setShowCatalogSyncModal}
              refetchCatalogs={() =>
                refetchCatalogs({
                  key: selectedFolderKey,
                  search: searchValue,
                  filterItems: 'all',
                  sort: sortValue,
                })
              }
              onAddXmlLink={() => {
                setOpenFromSyncModal(true);
                setShowCatalogSyncModal(false);
                setShowImportCatalogModal(true);
              }}
            />
          </div>
        )}
      </>
    );
  }
);

/**
 * Default props
 */
CatalogTab.defaultProps = {
  searchValue: '',
  loading: false,
  disabledFilter: false,
  catalogs: [],
  deletedCatalogs: [],
  config: {},
  isLocked: false,
  catalogThumbnails: [],
  showImportCatalogModal: false,
  setShowImportCatalogModal: () => {},
  showCatalogSyncModal: false,
  setShowCatalogSyncModal: () => {},
};

/**
 * Prop types
 */
CatalogTab.propTypes = {
  catalogs: PropTypes.arrayOf(PropTypes.shape({})),
  deletedCatalogs: PropTypes.arrayOf(PropTypes.string),
  searchValue: PropTypes.string,
  loading: PropTypes.bool,
  disabledFilter: PropTypes.bool,
  user: PropTypes.instanceOf(Object).isRequired,
  config: PropTypes.instanceOf(Object),
  saveUser: PropTypes.func.isRequired,
  sortValue: PropTypes.string.isRequired,
  teamMembers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLocked: PropTypes.bool,
  catalogThumbnails: PropTypes.array,
  setCatalogThumbnails: PropTypes.func,
  showImportCatalogModal: PropTypes.bool,
  setShowImportCatalogModal: PropTypes.func,
  showCatalogSyncModal: PropTypes.bool,
  setShowCatalogSyncModal: PropTypes.func,
};

export default CatalogTab;
